import React, { useState, useEffect, useMemo } from 'react';
import NotificationService from '../../services/NotificationService';
import util from "../../utils/util";
import { RawHTML } from "../../utils/Controls";
import { 
    Button, 
    Modal, 
    message, 
    Alert, 
    Switch, 
    Card, 
    Statistic, 
    Progress, 
    DatePicker, 
    Select,
    Row,
    Col,
    Tabs,
    Table,
    Tag
} from 'antd';
import { 
    BellOutlined, 
    FlagOutlined, 
    BarChartOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    RiseOutlined
} from '@ant-design/icons';
import './Notifications.css';
import useActivityLogger from '../../pages/useActivityLogger';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

export default function Index() {
    const [notifications, setNotifications] = useState([]);
    const [groupedNotifications, setGroupedNotifications] = useState({});
    const [newNotificationCounts, setNewNotificationCounts] = useState({});
    const [dtl, setDtl] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [bellShake, setBellShake] = useState(false);
    const [dateRange, setDateRange] = useState([null, null]);
    const [selectedType, setSelectedType] = useState('all');
    const [activeTab, setActiveTab] = useState('notifications');

    useActivityLogger();

    // Enhanced analytics calculations
    const analytics = useMemo(() => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        const thisWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        const thisMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

        const filteredNotifications = notifications.filter(notification => {
            const notifDate = new Date(notification.created);
            const matchesDateRange = !dateRange[0] || !dateRange[1] || 
                (notifDate >= dateRange[0] && notifDate <= dateRange[1]);
            const matchesType = selectedType === 'all' || notification.type === selectedType;
            return matchesDateRange && matchesType;
        });

        // Basic metrics
        const totalNotifications = filteredNotifications.length;
        const unreadCount = filteredNotifications.filter(n => !n.isread).length;
        const readCount = totalNotifications - unreadCount;
        const readRate = totalNotifications > 0 ? (readCount / totalNotifications * 100) : 0;

        // Time-based metrics
        const todayCount = filteredNotifications.filter(n => 
            new Date(n.created) >= today).length;
        const yesterdayCount = filteredNotifications.filter(n => {
            const date = new Date(n.created);
            return date >= yesterday && date < today;
        }).length;
        const weekCount = filteredNotifications.filter(n => 
            new Date(n.created) >= thisWeek).length;
        const monthCount = filteredNotifications.filter(n => 
            new Date(n.created) >= thisMonth).length;

        // Type distribution
        const typeDistribution = filteredNotifications.reduce((acc, notification) => {
            acc[notification.type] = (acc[notification.type] || 0) + 1;
            return acc;
        }, {});

        // Response time analytics
        const readNotifications = filteredNotifications.filter(n => n.isread);
        const responseTimes = readNotifications.map(notification => {
            const created = new Date(notification.created);
            const read = new Date(notification.updated || notification.created);
            return (read - created) / (1000 * 60 * 60); // hours
        });

        const avgResponseTime = responseTimes.length > 0 ? 
            responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length : 0;

        // Priority analytics (based on age and read status)
        const criticalNotifications = filteredNotifications.filter(n => {
            const age = (now - new Date(n.created)) / (1000 * 60 * 60 * 24);
            return !n.isread && age > 2;
        }).length;

        const urgentNotifications = filteredNotifications.filter(n => {
            const age = (now - new Date(n.created)) / (1000 * 60 * 60 * 24);
            return !n.isread && age > 1 && age <= 2;
        }).length;

        // Trend analysis
        const growthRate = yesterdayCount > 0 ? 
            ((todayCount - yesterdayCount) / yesterdayCount * 100) : 0;

        // Performance by type
        const typePerformance = Object.keys(typeDistribution).map(type => {
            const typeNotifications = filteredNotifications.filter(n => n.type === type);
            const typeUnread = typeNotifications.filter(n => !n.isread).length;
            const typeReadRate = typeNotifications.length > 0 ? 
                ((typeNotifications.length - typeUnread) / typeNotifications.length * 100) : 0;
            
            const typeResponseTimes = typeNotifications
                .filter(n => n.isread)
                .map(n => {
                    const created = new Date(n.created);
                    const read = new Date(n.updated || n.created);
                    return (read - created) / (1000 * 60 * 60);
                });
            
            const avgTypeResponseTime = typeResponseTimes.length > 0 ?
                typeResponseTimes.reduce((a, b) => a + b, 0) / typeResponseTimes.length : 0;

            return {
                type,
                total: typeNotifications.length,
                unread: typeUnread,
                readRate: typeReadRate,
                avgResponseTime: avgTypeResponseTime
            };
        });

        return {
            totalNotifications,
            unreadCount,
            readCount,
            readRate,
            todayCount,
            yesterdayCount,
            weekCount,
            monthCount,
            typeDistribution,
            avgResponseTime,
            criticalNotifications,
            urgentNotifications,
            growthRate,
            typePerformance
        };
    }, [notifications, dateRange, selectedType]);

    // Fetch notifications
    const list = () => {
        util.showLoader();
        NotificationService.all().then(({ data }) => {
            const notifications = data.result;
            setNotifications(notifications);
            groupNotifications(notifications);
            calculateNewNotificationCounts(notifications);
            setBellShake(true);
            setTimeout(() => setBellShake(false), 500);
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        });
    };

    // Check notification status
    const checkNotificationStatus = (notification) => {
        const currentTime = new Date();
        const notificationTime = new Date(notification.created);
        const timeDifference = (currentTime - notificationTime) / (1000 * 60 * 60 * 24);
        return !notification.isread && timeDifference > 2;
    };

    // Group notifications by type
    const groupNotifications = (notifications) => {
        const grouped = notifications.reduce((acc, notification) => {
            const { type } = notification;
            if (!acc[type]) {
                acc[type] = [];
            }
            acc[type].push({
                ...notification,
                isRed: checkNotificationStatus(notification)
            });
            return acc;
        }, {});
        setGroupedNotifications(grouped);
    };

    // Calculate new notification counts
    const calculateNewNotificationCounts = (notifications) => {
        const counts = notifications.reduce((acc, notification) => {
            if (!notification.isread) {
                const { type } = notification;
                if (!acc[type]) {
                    acc[type] = 0;
                }
                acc[type]++;
            }
            return acc;
        }, {});
        setNewNotificationCounts(counts);
    };

    // Mark notification as read
    const read = (rob, type, index) => {
        setDtl({ ...rob });

        if (!rob.isread) {
            NotificationService.read(rob.id).then(({ data }) => {
                const updatedNotifications = notifications.map((notification) =>
                    notification.id === rob.id ? { ...notification, isread: true, updated: new Date().toISOString() } : notification
                );
                setNotifications(updatedNotifications);
                groupNotifications(updatedNotifications);
                calculateNewNotificationCounts(updatedNotifications);
            }).catch(e => {
                message.error(e.message);
            });
        }
    };

    // Get background color based on notification type
    const getBackgroundColor = (type) => {
        switch (type) {
            case 'StockRequest':
                return '#f0f8ff';
            case 'StockTransfer':
                return '#f5f5dc';
            case 'StockReceive':
                return '#e6e6fa';
            case 'Ticket':
                return '#ffe4e1';
            default:
                return '#ffffff';
        }
    };

    // Get priority color
    const getPriorityColor = (notification) => {
        const age = (new Date() - new Date(notification.created)) / (1000 * 60 * 60 * 24);
        if (!notification.isread && age > 2) return '#ff4d4f'; // Critical - red
        if (!notification.isread && age > 1) return '#fa8c16'; // Urgent - orange
        if (!notification.isread) return '#1890ff'; // Normal - blue
        return '#52c41a'; // Read - green
    };

    const getPriorityText = (notification) => {
        const age = (new Date() - new Date(notification.created)) / (1000 * 60 * 60 * 24);
        if (!notification.isread && age > 2) return 'Critical';
        if (!notification.isread && age > 1) return 'Urgent';
        if (!notification.isread) return 'Normal';
        return 'Read';
    };

    useEffect(() => {
        list();
    }, []);

    const renderAnalyticsDashboard = () => (
        <div className="analytics-dashboard">
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Total Notifications"
                            value={analytics.totalNotifications}
                            prefix={<BellOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Unread"
                            value={analytics.unreadCount}
                            prefix={<ExclamationCircleOutlined />}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Read Rate"
                            value={analytics.readRate}
                            precision={1}
                            suffix="%"
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: analytics.readRate > 80 ? '#3f8600' : '#cf1322' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Avg Response Time"
                            value={analytics.avgResponseTime}
                            precision={1}
                            suffix="hrs"
                            prefix={<ClockCircleOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Today"
                            value={analytics.todayCount}
                            prefix={<RiseOutlined />}
                        />
                        <div style={{ marginTop: 8 }}>
                            <Tag color={analytics.growthRate > 0 ? 'red' : 'green'}>
                                {analytics.growthRate > 0 ? '+' : ''}{analytics.growthRate.toFixed(1)}% vs yesterday
                            </Tag>
                        </div>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="This Week"
                            value={analytics.weekCount}
                            prefix={<BarChartOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Critical (>2 days)"
                            value={analytics.criticalNotifications}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Urgent (1-2 days)"
                            value={analytics.urgentNotifications}
                            valueStyle={{ color: '#fa8c16' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Card title="Type Distribution" style={{ height: 300 }}>
                        {Object.entries(analytics.typeDistribution).map(([type, count]) => (
                            <div key={type} style={{ marginBottom: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{type}</span>
                                    <span>{count}</span>
                                </div>
                                <Progress 
                                    percent={(count / analytics.totalNotifications * 100)} 
                                    showInfo={false}
                                    strokeColor={getBackgroundColor(type)}
                                />
                            </div>
                        ))}
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Performance by Type" style={{ height: 300 }}>
                        <Table
                            dataSource={analytics.typePerformance}
                            columns={[
                                { title: 'Type', dataIndex: 'type', key: 'type' },
                                { title: 'Total', dataIndex: 'total', key: 'total' },
                                { title: 'Unread', dataIndex: 'unread', key: 'unread' },
                                { 
                                    title: 'Read Rate', 
                                    dataIndex: 'readRate', 
                                    key: 'readRate',
                                    render: (rate) => `${rate.toFixed(1)}%`
                                },
                                { 
                                    title: 'Avg Response', 
                                    dataIndex: 'avgResponseTime', 
                                    key: 'avgResponseTime',
                                    render: (time) => `${time.toFixed(1)}h`
                                }
                            ]}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );

    const renderNotifications = () => (
        <div>
            <div className="page-head uc d-flex" style={{ marginBottom: 24 }}>
                <div className="my-auto">
                    <h2>Notifications <span className="badge badge-primary">{analytics.unreadCount}</span></h2>
                </div>
                <div className="my-auto ml-auto" style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <RangePicker onChange={setDateRange} />
                    <Select value={selectedType} onChange={setSelectedType} style={{ width: 150 }}>
                        <Option value="all">All Types</Option>
                        {Object.keys(analytics.typeDistribution).map(type => (
                            <Option key={type} value={type}>{type}</Option>
                        ))}
                    </Select>
                    <div className={`notification-bell ${bellShake ? 'shake' : ''}`} onClick={list}>
                        <BellOutlined />
                        <span className="badge">{analytics.unreadCount}</span>
                    </div>
                    <Switch checked={showAll} onChange={() => setShowAll(!showAll)} /> Show All
                </div>
            </div>

            {/* Analytics Dashboard */}
            {renderAnalyticsDashboard()}

            <div className="grid-container">
                {Object.keys(groupedNotifications).map((type) => (
                    <div key={type} className="grid-item">
                        <h3>{type} <span className="badge badge-secondary">{newNotificationCounts[type] || 0}</span></h3>
                        {groupedNotifications[type]
                            .filter(notification => showAll || !notification.isread)
                            .filter(notification => selectedType === 'all' || notification.type === selectedType)
                            .filter(notification => {
                                if (!dateRange[0] || !dateRange[1]) return true;
                                const notifDate = new Date(notification.created);
                                return notifDate >= dateRange[0] && notifDate <= dateRange[1];
                            })
                            .map((notification, index) => {
                                const backgroundColor = getBackgroundColor(type);
                                const priorityColor = getPriorityColor(notification);
                                const priorityText = getPriorityText(notification);
                                
                                return (
                                    <div 
                                        key={index} 
                                        className={`mb10 notification-item ${notification.isread ? 'border' : 'unread'}`} 
                                        style={{ 
                                            backgroundColor, 
                                            borderLeft: `4px solid ${priorityColor}`,
                                            position: 'relative'
                                        }}
                                    >
                                        {/* Priority indicator */}
                                        <div style={{ 
                                            position: 'absolute', 
                                            top: 8, 
                                            right: 8,
                                            fontSize: '12px'
                                        }}>
                                            <Tag color={priorityColor}>{priorityText}</Tag>
                                        </div>

                                        {/* Red flag for unread notifications */}
                                        {!notification.isread && (
                                            <div className="red-flag">
                                                <FlagOutlined />
                                            </div>
                                        )}
                                        
                                        <Alert
                                            message={
                                                <div 
                                                    className={`cpointer ${notification.isread ? '' : 'bold600'}`} 
                                                    onClick={() => read(notification, type, index)}
                                                >
                                                    {notification.subject}
                                                </div>
                                            }
                                            description={
                                                <div>
                                                    <div>@ {notification.created}</div>
                                                    {notification.isread && notification.updated && (
                                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                                            Read: {notification.updated}
                                                        </div>
                                                    )}
                                                </div>
                                            }
                                            type={notification.isread ? "" : "info"}
                                            action={
                                                <Button size="small" type="primary" onClick={() => read(notification, type, index)}>
                                                    View
                                                </Button>
                                            }
                                        />
                                    </div>
                                );
                            })}
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="page-content">
            {renderNotifications()}

            <Modal
                title="Notification Detail"
                open={dtl !== null}
                okText="Go There"
                cancelText="Close"
                onOk={() => {
                    let url = '';
                    if (dtl.type === "StockRequest") {
                        url = `${url}/stock-request?ids=${dtl.type_ids}`;
                    }
                    if (dtl.type === "StockTransfer") {
                        url = `${url}/transfer-out?ids=${dtl.type_ids}`;
                    }
                    if (dtl.type === "StockReceive") {
                        url = `${url}/transfer-in?ids=${dtl.type_ids}`;
                    }
                    if (dtl.type === "Ticket") {
                        url = `${url}/job-cards?ids=${dtl.type_ids}`;
                    }
                    if (url) {
                        window.open(url, "_self");
                    }
                    setDtl(null);
                }}
                onCancel={() => setDtl(null)}
                destroyOnClose
                maskClosable={false}
                width={800}
            >
                {dtl !== null &&
                    <div>
                        <div className="bold600 mb5">{dtl.subject}</div>
                        <div>
                            <RawHTML html={dtl.msg} />
                        </div>
                        <div className="pt20">
                            <div>Notification Datetime: {dtl.created}</div>
                            {dtl.updated && (
                                <div>Read Datetime: {dtl.updated}</div>
                            )}
                            <div>Priority: <Tag color={getPriorityColor(dtl)}>{getPriorityText(dtl)}</Tag></div>
                        </div>
                    </div>}
            </Modal>
        </div>
    );
}                 